import authService from './authService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.geonexs.com';

class ApiService {

  async get(endpoint) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader(),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          const refreshResult = await authService.refreshToken();
          if (refreshResult.success) {
            const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...authService.getAuthHeader(),
              },
            });

            if (!retryResponse.ok) {
              await authService.logout();
              window.location.href = '/login';
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }

            return await retryResponse.json();
          } else {
            await authService.logout();
            window.location.href = '/login';
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async post(endpoint, data) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader(),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        if (response.status === 401) {
          const refreshResult = await authService.refreshToken();
          if (refreshResult.success) {
            const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...authService.getAuthHeader(),
              },
              body: JSON.stringify(data),
            });

            if (!retryResponse.ok) {
              await authService.logout();
              window.location.href = '/login';
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }

            return await retryResponse.json();
          } else {
            await authService.logout();
            window.location.href = '/login';
          }
        }

        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          throw new Error(response.statusText || `HTTP error! status: ${response.status}`);
        }

        if (response.status === 422 && errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const errorMessages = errorData.detail.map(err => {
              if (err.loc && err.msg) {
                const field = err.loc[err.loc.length - 1];
                return `${field}: ${err.msg}`;
              }
              return err.msg || 'Validation error';
            });
            throw new Error(errorMessages.join(', '));
          } else if (typeof errorData.detail === 'string') {
            throw new Error(errorData.detail);
          }
        }

        if (response.status === 500 && errorData.detail && errorData.detail.includes('duplicate key value violates unique constraint')) {
          const match = errorData.detail.match(/Key \(([^)]+)\)=\(([^)]+)\) already exists/);
          if (match) {
            const field = match[1];
            const value = match[2];
            throw new Error(`The ${field} "${value}" already exists. Please use a different value.`);
          }
          throw new Error('This record already exists. Please use different values.');
        }

        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async put(endpoint, data) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader(),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        if (response.status === 401) {
          const refreshResult = await authService.refreshToken();
          if (refreshResult.success) {
            const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                ...authService.getAuthHeader(),
              },
              body: JSON.stringify(data),
            });

            if (!retryResponse.ok) {
              await authService.logout();
              window.location.href = '/login';
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }

            return await retryResponse.json();
          } else {
            await authService.logout();
            window.location.href = '/login';
          }
        }

        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          throw new Error(response.statusText || `HTTP error! status: ${response.status}`);
        }

        if (response.status === 422 && errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const errorMessages = errorData.detail.map(err => {
              if (err.loc && err.msg) {
                const field = err.loc[err.loc.length - 1];
                return `${field}: ${err.msg}`;
              }
              return err.msg || 'Validation error';
            });
            throw new Error(errorMessages.join(', '));
          } else if (typeof errorData.detail === 'string') {
            throw new Error(errorData.detail);
          }
        }

        if (response.status === 500 && errorData.detail && errorData.detail.includes('duplicate key value violates unique constraint')) {
          const match = errorData.detail.match(/Key \(([^)]+)\)=\(([^)]+)\) already exists/);
          if (match) {
            const field = match[1];
            const value = match[2];
            throw new Error(`The ${field} "${value}" already exists. Please use a different value.`);
          }
          throw new Error('This record already exists. Please use different values.');
        }

        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async patch(endpoint, data) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader(),
        },
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        if (response.status === 401) {
          const refreshResult = await authService.refreshToken();
          if (refreshResult.success) {
            const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                ...authService.getAuthHeader(),
              },
              body: data ? JSON.stringify(data) : undefined,
            });

            if (!retryResponse.ok) {
              await authService.logout();
              window.location.href = '/login';
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }

            return await retryResponse.json();
          } else {
            await authService.logout();
            window.location.href = '/login';
          }
        }

        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          throw new Error(response.statusText || `HTTP error! status: ${response.status}`);
        }

        if (response.status === 422 && errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const errorMessages = errorData.detail.map(err => {
              if (err.loc && err.msg) {
                const field = err.loc[err.loc.length - 1];
                return `${field}: ${err.msg}`;
              }
              return err.msg || 'Validation error';
            });
            throw new Error(errorMessages.join(', '));
          } else if (typeof errorData.detail === 'string') {
            throw new Error(errorData.detail);
          }
        }

        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      try {
        return await response.json();
      } catch (e) {
        return { success: true };
      }
    } catch (error) {
      throw error;
    }
  }

  async delete(endpoint) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader(),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          const refreshResult = await authService.refreshToken();
          if (refreshResult.success) {
            const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
                ...authService.getAuthHeader(),
              },
            });

            if (!retryResponse.ok) {
              await authService.logout();
              window.location.href = '/login';
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }

            try {
              return await retryResponse.json();
            } catch (e) {
              return { success: true };
            }
          } else {
            await authService.logout();
            window.location.href = '/login';
          }
        }

        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();

          if (response.status === 500 && errorData.detail && errorData.detail.includes('duplicate key value violates unique constraint')) {
            const match = errorData.detail.match(/Key \(([^)]+)\)=\(([^)]+)\) already exists/);
            if (match) {
              const field = match[1];
              const value = match[2];
              errorMessage = `The ${field} "${value}" already exists. Please use a different value.`;
            } else {
              errorMessage = 'This record already exists. Please use different values.';
            }
          } else {
            errorMessage = errorData.detail || errorMessage;
          }
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      try {
        return await response.json();
      } catch (e) {
        return { success: true };
      }
    } catch (error) {
      throw error;
    }
  }

  async uploadFile(endpoint, formData) {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeader(),
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          const refreshResult = await authService.refreshToken();
          if (refreshResult.success) {
            const retryResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
              method: 'POST',
              headers: {
                ...authService.getAuthHeader(),
              },
              body: formData,
            });

            if (!retryResponse.ok) {
              await authService.logout();
              window.location.href = '/login';
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }

            return await retryResponse.json();
          } else {
            await authService.logout();
            window.location.href = '/login';
          }
        }

        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          throw new Error(response.statusText || `HTTP error! status: ${response.status}`);
        }

        if (response.status === 422 && errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const errorMessages = errorData.detail.map(err => {
              if (err.loc && err.msg) {
                const field = err.loc[err.loc.length - 1];
                return `${field}: ${err.msg}`;
              }
              return err.msg || 'Validation error';
            });
            throw new Error(errorMessages.join(', '));
          } else if (typeof errorData.detail === 'string') {
            throw new Error(errorData.detail);
          }
        }

        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
}

export default new ApiService();